import { LICENSE_STATUS, DEFAULT_LICENSE_SETTINGS, ACCESS_LEVELS, FEATURE_DEFAULTS } from '../constants/license.js';
import { calendarDaysBetween } from '../time/index.js';

const DAY_MS = 86_400_000;

/**
 * Single source of truth for license status, used by the server (enforcement)
 * and both UIs (banners, badges). `expiryDate` is the UTC instant of end-of-day
 * in the tenant timezone, so the expiry day itself is fully usable.
 *
 *   suspended                        → suspended
 *   now <  expiry − bannerDays       → active
 *   now <= expiry                    → expiring
 *   now <= expiry + graceDays        → grace
 *   otherwise                        → expired
 */
export function computeLicenseStatus(license, now = new Date(), { bannerDays } = {}) {
  if (!license || !license.expiryDate) return LICENSE_STATUS.EXPIRED;
  if (license.suspended) return LICENSE_STATUS.SUSPENDED;
  const window = (bannerDays ?? DEFAULT_LICENSE_SETTINGS.expiryBannerDays) * DAY_MS;
  const t = new Date(now).getTime();
  const expiry = new Date(license.expiryDate).getTime();
  if (t < expiry - window) return LICENSE_STATUS.ACTIVE;
  if (t <= expiry) return LICENSE_STATUS.EXPIRING;
  if (t <= graceEndsAt(license).getTime()) return LICENSE_STATUS.GRACE;
  return LICENSE_STATUS.EXPIRED;
}

/** A licence's features, with the default for any it doesn't mention (e.g. kot on older clients). */
export function withFeatureDefaults(features) {
  return { ...FEATURE_DEFAULTS, ...Object.fromEntries(Object.entries(features ?? {}).filter(([, v]) => v != null)) };
}

/** Whether a feature is on for these licence features. */
export function featureOn(features, key) {
  return Boolean(withFeatureDefaults(features)[key]);
}

/**
 * The kitchen for an outlet: `orders` when the plan includes kitchen orders (KOT) and the outlet
 * sends orders to a kitchen (Outlets → "Send orders to a kitchen"); `display` when the kitchen
 * display (KDS) is in the plan too. Without an outlet, only the plan decides.
 */
export function kitchenFor(features, outlet) {
  const outletSends = (outlet?.kitchenOrders ?? outlet?.pos?.kitchen?.enabled) !== false; // an outlet DTO or document
  const orders = featureOn(features, 'kot') && outletSends;
  return { orders, display: orders && featureOn(features, 'kds') };
}

export function graceEndsAt(license) {
  return new Date(new Date(license.expiryDate).getTime() + (license.graceDays ?? 0) * DAY_MS);
}

/**
 * What each status allows.
 *   login: 'all' | 'owner' | 'none'
 */
export const STATUS_CAPABILITIES = Object.freeze({
  active: { login: 'all', read: true, write: true, billing: true, mallApi: true },
  expiring: { login: 'all', read: true, write: true, billing: true, mallApi: true },
  grace: { login: 'all', read: true, write: true, billing: true, mallApi: true },
  expired: { login: 'owner', read: true, write: false, billing: false, mallApi: false },
  suspended: { login: 'none', read: false, write: false, billing: false, mallApi: false },
});

/** Is a user allowed `level` (login/read/write/billing) under `status`? */
export function canAccess(status, level, { isOwner = false } = {}) {
  const caps = STATUS_CAPABILITIES[status] ?? STATUS_CAPABILITIES.suspended;
  const loginOk = caps.login === 'all' || (caps.login === 'owner' && isOwner);
  if (!loginOk) return false;
  switch (level) {
    case ACCESS_LEVELS.LOGIN:
      return true;
    case ACCESS_LEVELS.READ:
      return caps.read;
    case ACCESS_LEVELS.WRITE:
      return caps.write;
    case ACCESS_LEVELS.BILLING:
      return caps.billing;
    default:
      return false;
  }
}

/** Everything a UI needs to render a license badge or banner. */
export function describeLicense(license, now = new Date(), { bannerDays, timezone = 'Asia/Kolkata' } = {}) {
  const status = computeLicenseStatus(license, now, { bannerDays });
  const daysLeft = license?.expiryDate ? calendarDaysBetween(now, license.expiryDate, timezone) : null;
  const graceEnd = license?.expiryDate ? graceEndsAt(license) : null;
  const graceDaysLeft = graceEnd ? calendarDaysBetween(now, graceEnd, timezone) : null;
  let banner = null;
  if (status === LICENSE_STATUS.EXPIRING) banner = { tone: 'warning', key: 'expiring' };
  else if (status === LICENSE_STATUS.GRACE) banner = { tone: 'danger', key: 'grace' };
  else if (status === LICENSE_STATUS.EXPIRED) banner = { tone: 'danger', key: 'expired' };
  else if (status === LICENSE_STATUS.SUSPENDED) banner = { tone: 'danger', key: 'suspended' };
  return {
    status,
    expiryDate: license?.expiryDate ?? null,
    graceEndsAt: graceEnd,
    daysLeft,
    graceDaysLeft,
    banner,
    capabilities: STATUS_CAPABILITIES[status],
  };
}
